import { useEffect, useCallback, useState } from 'react'
import { matchPath, useLocation } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { isMobileDevice, isBottomMenuVisible } from 'utils/helpers'
import { Routes } from 'utils/routes'
import { bootAsUser, bootAnonymous } from 'utils/intercom'
import { setActiveChatProvider, hideLauncher, showLauncher, setLanguage } from 'utils/chat'
import { initZendesk, onZendeskReady } from 'utils/zendesk'
import { CHAT_PROVIDERS, getIntercomUserId } from 'utils/constants'
import { getItem } from 'utils/storageUtils'

/**
 * ZohoChatButtonControl
 *
 * Headless component (renders null) that is the **single source of truth**
 * for hiding and showing the floating chat launcher based on route, viewport,
 * and modal state. Provider-agnostic: the active provider (Intercom / Zendesk /
 * Zoho) comes from the API `chatProvider` payload, is pushed to the `utils/chat`
 * facade via `setActiveChatProvider()`, and visibility calls go through that
 * facade. Intercom-only concerns (authenticated boot + identity hashing) and
 * Zendesk snippet injection are gated on the resolved provider.
 *
 * VISIBILITY STRATEGY — dual-layer (must stay in sync):
 *   1. CSS  — `body.hide-chat-mobile` (defined in default/index.scss)
 *             Fires synchronously and covers the brief gap while the widget
 *             re-renders its iframe after boot/update events.
 *   2. JS   — `hideLauncher()` / `showLauncher()` from utils/chat.js
 *             Persists the setting inside the provider's own state.
 *
 * HIDE triggers (mobile only):
 *   • `general.hideDefaultChatLauncherMobile = true` — tenant always hides the
 *     default launcher on mobile (they use the MobileBottomMenu Chat button instead).
 *     Only applied below 992px, where MobileBottomMenu is actually rendered.
 *   • User is on a game / sportsbook route
 *   • A modal is open AND `general.hideChatOnModalOpen = true`
 *
 * SHOW triggers:
 *   • Desktop — always visible (never hidden here)
 *   • Mobile — when none of the hide conditions above apply AND
 *     `hideDefaultChatLauncherMobile` is false
 */
// Static list — defined once at module level, not recreated on every render
const GAME_ROUTES = [
  Routes.playGame,
  Routes.mobilePlayGame,
  Routes.demoGame,
  Routes.sportsbook
]

const ZohoChatButtonControl = () => {
  const location = useLocation()
  const { modals } = useSelector((state) => state.modal)
  const { general } = useSelector((state) => state.uiConfig)
  const { selectedLanguageCode, chatProvider } = useSelector((state) => state.language)
  const { userDetails } = useSelector((state) => state.user)
  const loggedIn = JSON.parse(getItem('loggedIn') || 'false')

  // Active provider comes from the API payload (decrypted chatProvider), not env.
  const provider = chatProvider?.chatProvider

  // Zendesk's snippet loads async, so window.zE may not exist immediately after
  // injection. Track readiness so visibility + locale re-apply once it's available.
  const [zendeskReady, setZendeskReady] = useState(false)

  /**
   * Resolve the active provider from the API payload: expose it to the chat
   * facade (so non-React callers like the game-screen handlers can use it) and
   * inject the Zendesk snippet at runtime when Zendesk is the provider.
   */
  useEffect(() => {
    setActiveChatProvider(provider)
    if (provider === CHAT_PROVIDERS.ZENDESK) {
      initZendesk(chatProvider?.ZENDESK_MESSAGING_WIDGET_KEY)
    }
  }, [provider, chatProvider?.ZENDESK_MESSAGING_WIDGET_KEY])

  useEffect(() => {
    if (provider !== CHAT_PROVIDERS.ZENDESK) return
    return onZendeskReady(() => setZendeskReady(true))
  }, [provider])

  /**
   * Initialize Intercom when user logs in or out, or when chatProvider arrives from API.
   * chatProvider is included in deps so that bootAnonymous fires on initial guest page
   * load — the API response may arrive after the first render when userId/loggedIn are
   * still falsy, so without chatProvider the boot would never happen for guests.
   *
   * RACE CONDITION GUARD:
   *   When a user logs in, `loggedIn` flips to true immediately (from localStorage)
   *   but `userDetails` arrives from the API a moment later. During that gap:
   *     - (loggedIn && userDetails) = false  ← would fall into the else branch
   *     - else branch calls bootAnonymous()  ← calls shutdown() + wipes the session
   *     - then userDetails arrives → bootAsUser() fires → but Intercom server already
   *       saw an extra shutdown/anonymous-boot, breaking conversation history
   *   Fix: skip bootAnonymous entirely when loggedIn=true (user is mid-login, not a guest).
   */
  useEffect(() => {
    // Intercom-only: Zendesk runs anonymously and needs no boot/identity.
    if (provider !== CHAT_PROVIDERS.INTERCOM) return
    if (!chatProvider?.INTERCOM_APP_ID) return

    if (loggedIn && userDetails) {
      // Guard: do not boot with an incomplete identity — userId must be present [AMGP-2994]
      // to avoid sending an undefined chat user identifier.
      if (!userDetails?.userId) return

      if (chatProvider?.chatProvider === CHAT_PROVIDERS.INTERCOM) {
        // user_hash (hex HMAC of the id) is signed entirely by the backend — get-user-details
        // `intercomUserHash` — so the Intercom secret never reaches the browser. Without it Intercom
        // would reject the identity, so fall back to an anonymous session.
        const userHash = userDetails?.intercomUserHash
        if (!userHash) {
          bootAnonymous({ appId: chatProvider?.INTERCOM_APP_ID, languageCode: selectedLanguageCode })
          return
        }
        bootAsUser({
          appId: chatProvider?.INTERCOM_APP_ID,
          userId: getIntercomUserId(userDetails.userId),
          name: `${userDetails?.firstName} ${userDetails?.lastName}`,
          email: userDetails?.email,
          createdAt: userDetails?.createdAt,
          userHash,
          tags: userDetails?.tags,
          languageCode: selectedLanguageCode
        })
      }
    } else if (!loggedIn) {
      // Only boot anonymously when the user is genuinely a guest (loggedIn=false).
      // If loggedIn=true but userDetails hasn't arrived yet, we are mid-login —
      // do NOT call bootAnonymous here or it will shutdown() the session before
      // bootAsUser() gets a chance to establish the authenticated identity.
      if (chatProvider?.chatProvider === CHAT_PROVIDERS.INTERCOM) {
        bootAnonymous({ appId: chatProvider?.INTERCOM_APP_ID, languageCode: selectedLanguageCode })
      }
    }
  }, [userDetails?.userId, userDetails?.intercomUserHash, loggedIn, chatProvider?.INTERCOM_APP_ID])

  /**
   * Sync the widget language with the user's selected app language.
   * Works for both providers via the facade. Re-runs when the provider resolves
   * (so Intercom re-applies on chatProvider arrival, matching prior behavior) and
   * once Zendesk is ready (so the initial locale applies after the snippet loads).
   */
  useEffect(() => {
    if (selectedLanguageCode) setLanguage(selectedLanguageCode)
  }, [selectedLanguageCode, zendeskReady, provider])

  const updateVisibility = useCallback(() => {
    const pathname = location.pathname
    const isMobile = isMobileDevice()

    // Config flags — the bottom-menu Chat button only replaces the launcher where the menu is shown
    const alwaysHideOnMobile = !!general?.hideDefaultChatLauncherMobile && isBottomMenuVisible()
    const hideChatOnModalOpen = !!general?.hideChatOnModalOpen

    // Context flags
    const isOnGameRoute = GAME_ROUTES.some((pattern) =>
      matchPath({ path: pattern, end: true }, pathname)
    )
    const isModalOpen = modals?.length > 0

    // Body class for the Zoho chat button (game screen body class)
    if (isMobile && isOnGameRoute) {
      document.body.classList.add('mobile-play-game')
    } else {
      document.body.classList.remove('mobile-play-game')
    }

    const shouldHide =
      isMobile && (alwaysHideOnMobile || isOnGameRoute || (isModalOpen && hideChatOnModalOpen))

    if (shouldHide) {
      // CSS layer — instant synchronous hide
      document.body.classList.add('hide-chat-mobile')

      // Zoho hide
      window?.$zoho?.salesiq?.chatbutton?.visible?.('hide')

      // Active provider hide (async; CSS layer covers the gap)
      hideLauncher()
    } else if (!isMobile || !alwaysHideOnMobile) {
      // Restore visibility:
      //   • Always on desktop
      //   • On mobile devices that do NOT have hideDefaultChatLauncherMobile set
      document.body.classList.remove('hide-chat-mobile')
      document.body.classList.remove('hide-chat')

      // Zoho show
      window?.$zoho?.salesiq?.chatbutton?.visible?.('show')

      // Active provider show
      showLauncher()
    }
  }, [location.pathname, modals, general])

  // Re-evaluate on route/modal/config changes, when the provider resolves, and
  // once Zendesk becomes ready — so the JS hide/show layer re-applies after the
  // active provider is known (the body-class CSS layer already applies instantly).
  useEffect(() => {
    updateVisibility()
  }, [updateVisibility, zendeskReady, provider])

  // Re-evaluate on window resize (mobile ↔ desktop crossing)
  useEffect(() => {
    window.addEventListener('resize', updateVisibility)
    return () => window.removeEventListener('resize', updateVisibility)
  }, [updateVisibility])

  /**
   * `intercom-booted` listener — Intercom fires this custom event immediately
   * after the SDK boots. At that point Intercom would normally show its launcher.
   * If the tenant always hides it on mobile, we suppress it right here before
   * the user ever sees a flash. (Zendesk relies on the CSS layer for anti-flash.)
   */
  useEffect(() => {
    if (provider !== CHAT_PROVIDERS.INTERCOM) return
    if (!general?.hideDefaultChatLauncherMobile) return

    const handleIntercomBoot = () => {
      if (isBottomMenuVisible()) {
        hideLauncher()
      }
    }

    window.addEventListener('intercom-booted', handleIntercomBoot)
    return () => window.removeEventListener('intercom-booted', handleIntercomBoot)
  }, [provider, general?.hideDefaultChatLauncherMobile])

  return null
}

export default ZohoChatButtonControl
